/**
 * SiteEditorCard — Settings → Sites tab.
 *
 * Form-based editor for the workspace's public marketing site. Section
 * toggles, per-section forms, theme picker, color picker, "AI describe your
 * vision" generator, publish toggle, and a copy/open public URL.
 *
 * One site per workspace (MVP). Multi-site comes later.
 */
import { useEffect, useState } from 'react';
import {
  ExternalLink, Copy, Check, Sparkles, Eye, Plus, Trash2, Globe, Loader2,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { apiFetch } from '../../fetchInterceptor';
import { toast } from 'sonner';
import SiteAIChat from './SiteAIChat';
import CustomDomainCard from './CustomDomainCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const THEMES = [
  { id: 'professional', label: 'Professional', sub: 'Clean, corporate, serif headlines. Best for finance, legal, medical.' },
  { id: 'warm',         label: 'Warm',         sub: 'Friendly, rounded, earth-tones. Best for service trades and hospitality.' },
  { id: 'bold',         label: 'Bold',         sub: 'Large hero, vivid color, sans-serif. Best for creative & agencies.' },
];

const COLOR_PRESETS = [
  '#10b981', '#3b82f6', '#8b5cf6', '#f59e0b',
  '#ec4899', '#06b6d4', '#ef4444', '#0f172a',
];

export default function SiteEditorCard({ currentWorkspace }) {
  const wsId = currentWorkspace?.workspace_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiVision, setAiVision] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  const [openSection, setOpenSection] = useState('hero');
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await apiFetch(`${API_URL}/api/sites/me?workspace_id=${wsId}`);
        if (r.ok) setData(await r.json());
      } finally {
        setLoading(false);
      }
    })();
  }, [wsId]);

  const updateSection = (key, patch) => {
    setData((d) => ({
      ...d,
      site: { ...d.site, sections: { ...d.site.sections, [key]: { ...d.site.sections[key], ...patch } } },
    }));
  };

  const updateSite = (patch) => {
    setData((d) => ({ ...d, site: { ...d.site, ...patch } }));
  };

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const body = {
        theme: data.site.theme,
        brand_color: data.site.brand_color,
        published: data.site.published,
        sections: data.site.sections,
        seo: data.site.seo,
        ...extra,
      };
      const r = await apiFetch(`${API_URL}/api/sites/me?workspace_id=${wsId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || 'Save failed');
        return false;
      }
      const next = await r.json();
      setData(next);
      toast.success('Site saved');
      return true;
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    const next = !data.site.published;
    updateSite({ published: next });
    const ok = await save({ published: next });
    if (ok) toast.success(next ? 'Site is live!' : 'Site unpublished');
  };

  const runAi = async () => {
    if (aiVision.trim().length < 20) {
      toast.error('Describe your business in at least 20 characters');
      return;
    }
    setAiGenerating(true);
    setAiPreview(null);
    try {
      const r = await apiFetch(`${API_URL}/api/sites/ai-generate?workspace_id=${wsId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vision: aiVision, model: localStorage.getItem('siteAiModel') || undefined }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || 'AI generation failed');
        return;
      }
      setAiPreview(await r.json());
    } finally {
      setAiGenerating(false);
    }
  };

  const applyAi = () => {
    if (!aiPreview) return;
    setData((d) => ({
      ...d,
      site: {
        ...d.site,
        theme: aiPreview.recommended_theme || d.site.theme,
        brand_color: aiPreview.recommended_color || d.site.brand_color,
        sections: {
          ...d.site.sections,
          hero: {
            ...d.site.sections.hero,
            visible: true,
            headline: aiPreview.headline || d.site.sections.hero.headline,
            subheadline: aiPreview.subheadline || d.site.sections.hero.subheadline,
          },
          about: {
            ...d.site.sections.about,
            visible: true,
            body: aiPreview.about || d.site.sections.about.body,
          },
          services: {
            ...d.site.sections.services,
            visible: aiPreview.services?.length > 0,
            items: aiPreview.services || d.site.sections.services.items,
          },
        },
        seo: {
          meta_title: aiPreview.meta_title || d.site.seo.meta_title,
          meta_description: aiPreview.meta_description || d.site.seo.meta_description,
        },
      },
    }));
    setAiOpen(false);
    setAiPreview(null);
    setAiVision('');
    toast.success('Applied! Now click Save to commit.');
  };

  if (loading || !data) {
    return <div className="animate-pulse h-96 bg-muted rounded-2xl" />;
  }

  const publicUrl = `${window.location.origin}/s/${data.slug}`;
  const brand = data.site.brand_color || '#10b981';
  const sections = data.site.sections;

  return (
    <div className="space-y-6" data-testid="site-editor">
      {/* ── Public URL bar ───────────────────────────────────────────── */}
      <div className="rounded-2xl border bg-card p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-5 h-5" style={{ color: brand }} />
              <h3 className="font-semibold text-lg">Your website</h3>
              {data.site.published ? (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">Live</span>
              ) : (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-zinc-500/15 text-zinc-600 dark:text-zinc-400">Draft</span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {data.site.published
                ? 'Your site is live and visible to anyone with the link.'
                : 'Save your changes, then click Publish to make it live.'}
            </p>
          </div>
          <Switch
            checked={data.site.published}
            onCheckedChange={togglePublish}
            data-testid="site-publish-toggle"
          />
        </div>
        <div className="flex gap-2">
          <Input value={publicUrl} readOnly className="font-mono text-sm" data-testid="site-public-url" />
          <Button
            variant="outline" size="sm"
            onClick={() => { navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success('Link copied'); }}
            data-testid="site-copy-url"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="site-open-url">
              <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
        </div>
      </div>

      {/* ── AI vision generator + chat editor ───────────────────── */}
      <div className="rounded-2xl border bg-gradient-to-br from-violet-50 to-blue-50 dark:from-violet-950/30 dark:to-blue-950/30 p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="font-semibold flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-400" />
              AI website builder
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Two ways to use AI — start from scratch with a vision, or chat to iterate on what you have.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setAiOpen(true)} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="site-ai-open">
            <Sparkles className="w-4 h-4 mr-1.5" />
            Describe your vision
          </Button>
          <Button
            onClick={() => setChatOpen(true)}
            variant="outline"
            className="border-violet-300 text-violet-700 dark:text-violet-300 hover:bg-violet-100/50 dark:hover:bg-violet-950/50"
            data-testid="site-ai-chat-open"
          >
            <Sparkles className="w-4 h-4 mr-1.5" />
            Chat with AI to edit
          </Button>
        </div>
      </div>

      {/* ── Theme picker ──────────────────────────────────────────── */}
      <div className="rounded-2xl border bg-card p-6">
        <h3 className="font-semibold text-lg mb-3">Theme</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {THEMES.map((th) => (
            <button
              key={th.id}
              type="button"
              onClick={() => updateSite({ theme: th.id })}
              className={`text-left rounded-xl border p-4 transition-all ${data.site.theme === th.id ? 'ring-2 ring-offset-2' : 'hover:bg-accent'}`}
              style={data.site.theme === th.id ? { borderColor: brand, ringColor: brand } : {}}
              data-testid={`site-theme-${th.id}`}
            >
              <p className="font-semibold mb-1">{th.label}</p>
              <p className="text-xs text-muted-foreground">{th.sub}</p>
            </button>
          ))}
        </div>

        <div className="mt-6">
          <Label>Brand color</Label>
          <div className="mt-2 flex items-center gap-3">
            <div className="flex gap-1.5">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c} type="button"
                  onClick={() => updateSite({ brand_color: c })}
                  className="w-8 h-8 rounded-lg ring-2 ring-offset-2"
                  style={{ background: c, ringColor: data.site.brand_color === c ? c : 'transparent' }}
                  data-testid={`site-color-${c.slice(1)}`}
                />
              ))}
            </div>
            <Input
              value={data.site.brand_color}
              onChange={(e) => updateSite({ brand_color: e.target.value })}
              className="font-mono w-32"
            />
          </div>
        </div>
      </div>

      {/* ── Sections ─────────────────────────────────────────────── */}
      {[
        { key: 'hero', label: 'Hero', fields: <HeroFields s={sections.hero} update={(p) => updateSection('hero', p)} /> },
        { key: 'trust', label: 'Trust bar (logos / credentials)', fields: <TrustFields s={sections.trust} update={(p) => updateSection('trust', p)} /> },
        { key: 'stats', label: 'Stats (numbers)', fields: <StatsFields s={sections.stats} update={(p) => updateSection('stats', p)} /> },
        { key: 'about', label: 'About', fields: <AboutFields s={sections.about} update={(p) => updateSection('about', p)} /> },
        { key: 'services', label: 'Services', fields: <ServicesFields s={sections.services} update={(p) => updateSection('services', p)} /> },
        { key: 'testimonials', label: 'Testimonials', fields: <TestimonialsFields s={sections.testimonials} update={(p) => updateSection('testimonials', p)} /> },
        { key: 'faq', label: 'FAQ', fields: <FAQFields s={sections.faq} update={(p) => updateSection('faq', p)} /> },
        { key: 'hours_contact', label: 'Hours & Contact Info', fields: <HoursContactFields s={sections.hours_contact} update={(p) => updateSection('hours_contact', p)} /> },
        { key: 'contact_form', label: 'Contact Form', fields: <ContactFormFields s={sections.contact_form} update={(p) => updateSection('contact_form', p)} /> },
        { key: 'footer', label: 'Footer', fields: <FooterFields s={sections.footer} update={(p) => updateSection('footer', p)} /> },
      ].map(({ key, label, fields }) => (
        <div key={key} className="rounded-2xl border bg-card overflow-hidden" data-testid={`site-section-${key}`}>
          <div className="w-full p-5 flex items-center justify-between hover:bg-accent/30 transition-colors">
            <div className="flex items-center gap-3">
              <Switch
                checked={sections[key]?.visible}
                onCheckedChange={(c) => { updateSection(key, { visible: c }); }}
                data-testid={`site-section-toggle-${key}`}
              />
              <button
                type="button"
                onClick={() => setOpenSection(openSection === key ? null : key)}
                className="font-semibold text-left hover:opacity-80 transition-opacity"
              >
                {label}
              </button>
              {!sections[key]?.visible && <span className="text-xs text-muted-foreground">Hidden</span>}
            </div>
            <button
              type="button"
              onClick={() => setOpenSection(openSection === key ? null : key)}
              className="p-1.5 rounded-md hover:bg-accent/50 transition-colors"
              aria-label={openSection === key ? 'Collapse' : 'Expand'}
            >
              {openSection === key ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
          {openSection === key && <div className="px-5 pb-5 space-y-3 border-t pt-5">{fields}</div>}
        </div>
      ))}

      {/* ── Custom domain ───────────────────────────────────────── */}
      <CustomDomainCard workspaceId={wsId} />

      {/* ── Sticky save bar ─────────────────────────────────────── */}
      <div className="sticky bottom-4 z-10 flex justify-end gap-2">
        <Button variant="outline" asChild>
          <a href={publicUrl} target="_blank" rel="noopener noreferrer">
            <Eye className="w-4 h-4 mr-1.5" />
            Preview
          </a>
        </Button>
        <Button
          onClick={() => save()}
          disabled={saving}
          size="lg"
          className="text-white shadow-lg"
          style={{ background: brand }}
          data-testid="site-save"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>

      {/* ── AI dialog (vision generator) ───────────────────────── */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-xl" data-testid="site-ai-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              Describe your business
            </DialogTitle>
            <DialogDescription>
              Tell us who you are, what you do, and who you serve. AI will draft a complete site you can edit.
            </DialogDescription>
          </DialogHeader>

          {!aiPreview ? (
            <div className="space-y-3">
              <Textarea
                value={aiVision}
                onChange={(e) => setAiVision(e.target.value)}
                rows={5}
                placeholder="E.g. 'I'm a CPA based in Austin specializing in tax planning and bookkeeping for tech startups. I've been doing this for 8 years. My clients are early-stage founders who need clean books for their seed/Series-A.'"
                data-testid="site-ai-vision"
              />
              <p className="text-xs text-muted-foreground">
                Be specific — the more detail, the better. Mention your services, who you serve, your tone, and any unique angle.
              </p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="rounded-lg border p-3 bg-zinc-50 dark:bg-zinc-900">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1">Headline</p>
                <p className="text-lg font-bold">{aiPreview.headline}</p>
                <p className="text-sm text-muted-foreground mt-1">{aiPreview.subheadline}</p>
              </div>
              <div className="rounded-lg border p-3 bg-zinc-50 dark:bg-zinc-900">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1">About</p>
                <p className="text-sm">{aiPreview.about}</p>
              </div>
              <div className="rounded-lg border p-3 bg-zinc-50 dark:bg-zinc-900">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Services ({aiPreview.services?.length || 0})</p>
                <ul className="space-y-2">
                  {(aiPreview.services || []).map((s, i) => (
                    <li key={i} className="text-sm">
                      <span className="font-semibold">{s.title}</span>
                      {s.price && <span className="ml-2 text-xs px-2 py-0.5 bg-zinc-200 dark:bg-zinc-700 rounded-full">{s.price}</span>}
                      <p className="text-xs text-muted-foreground">{s.description}</p>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center gap-3 rounded-lg border p-3 bg-zinc-50 dark:bg-zinc-900">
                <div className="w-10 h-10 rounded-lg" style={{ background: aiPreview.recommended_color }} />
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Recommended look</p>
                  <p className="text-sm font-semibold capitalize">{aiPreview.recommended_theme} · {aiPreview.recommended_color}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {!aiPreview ? (
              <Button onClick={runAi} disabled={aiGenerating} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="site-ai-generate">
                {aiGenerating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Drafting…</> : <><Sparkles className="w-4 h-4 mr-1.5" />Generate draft</>}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setAiPreview(null)}>Regenerate</Button>
                <Button onClick={applyAi} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="site-ai-apply">
                  Apply to my site
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Floating AI chat panel ──────────────────────────── */}
      {chatOpen && (
        <SiteAIChat
          workspaceId={wsId}
          site={data.site}
          onApply={async (newSite) => {
            setData((d) => ({ ...d, site: newSite }));
            await save({
              theme: newSite.theme,
              brand_color: newSite.brand_color,
              published: newSite.published,
              sections: newSite.sections,
              seo: newSite.seo,
            });
          }}
          onClose={() => setChatOpen(false)}
        />
      )}
    </div>
  );
}

// ── Section field components (kept small and inline) ────────────────────

function HeroFields({ s, update }) {
  return (
    <>
      <Field
        label="Eyebrow (small pill above headline)"
        value={s?.eyebrow}
        onChange={(v) => update({ eyebrow: v })}
        placeholder="TRUSTED BY 50+ STARTUPS"
      />
      <Field label="Headline" value={s?.headline} onChange={(v) => update({ headline: v })} placeholder="Modern accounting for modern businesses" />
      <Field label="Subheadline" value={s?.subheadline} onChange={(v) => update({ subheadline: v })} placeholder="One sentence supporting the headline" multiline />
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Primary button label" value={s?.cta_label} onChange={(v) => update({ cta_label: v })} placeholder="Get in touch" />
        <Field label="Primary button link" value={s?.cta_link} onChange={(v) => update({ cta_link: v })} placeholder="#contact" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Secondary button label (optional)" value={s?.secondary_cta_label} onChange={(v) => update({ secondary_cta_label: v })} placeholder="Learn more" />
        <Field label="Secondary button link" value={s?.secondary_cta_link} onChange={(v) => update({ secondary_cta_link: v })} placeholder="#about" />
      </div>
    </>
  );
}

function AboutFields({ s, update }) {
  return <Field label="About paragraph" value={s?.body} onChange={(v) => update({ body: v })} placeholder="Tell visitors who you are…" multiline rows={5} />;
}

const SERVICE_ICON_OPTIONS = [
  'briefcase', 'calculator', 'trending-up', 'shield-check', 'file-text', 'users',
  'bar-chart', 'clock', 'dollar-sign', 'globe', 'lightbulb', 'target',
  'rocket', 'heart', 'award', 'headphones',
];

function ServicesFields({ s, update }) {
  const items = s?.items || [];
  const setItems = (next) => update({ items: next });
  return (
    <>
      <Field label="Section title" value={s?.title} onChange={(v) => update({ title: v })} placeholder="Services" />
      <Field label="Section subtitle (optional)" value={s?.subtitle} onChange={(v) => update({ subtitle: v })} placeholder="A line that introduces what you do" />
      <div className="space-y-3">
        {items.map((svc, i) => (
          <div key={svc.id || i} className="rounded-lg border p-3 space-y-2">
            <div className="flex gap-2">
              <Input value={svc.title || ''} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} placeholder="Service title" className="flex-1" />
              <Input value={svc.price || ''} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} placeholder="$400/mo" className="w-32" />
              <Button type="button" variant="ghost" size="sm" onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-red-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <Textarea
              value={svc.description || ''}
              onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
              placeholder="Short description"
              rows={2}
            />
            <div className="grid sm:grid-cols-3 gap-2">
              <select
                value={svc.icon || 'briefcase'}
                onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, icon: e.target.value } : x))}
                className="h-9 px-2 text-sm rounded-md border bg-background"
                data-testid={`site-service-icon-${i}`}
              >
                {SERVICE_ICON_OPTIONS.map((ic) => (
                  <option key={ic} value={ic}>{ic}</option>
                ))}
              </select>
              <Input value={svc.cta_label || ''} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, cta_label: e.target.value } : x))} placeholder="Button label (optional)" />
              <Input value={svc.cta_link || ''} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, cta_link: e.target.value } : x))} placeholder="Stripe link (optional)" />
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { id: `svc_${Date.now().toString(36)}`, title: '', description: '', price: '', icon: 'briefcase' }])}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add service
        </Button>
      </div>
    </>
  );
}

function TestimonialsFields({ s, update }) {
  const items = s?.items || [];
  const setItems = (next) => update({ items: next });
  return (
    <div className="space-y-3">
      {items.map((q, i) => (
        <div key={q.id || i} className="rounded-lg border p-3 space-y-2">
          <Textarea
            value={q.quote || ''}
            onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, quote: e.target.value } : x))}
            placeholder="Their quote about you"
            rows={2}
          />
          <div className="flex gap-2">
            <Input value={q.author || ''} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, author: e.target.value } : x))} placeholder="Author name" />
            <Input value={q.role || ''} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, role: e.target.value } : x))} placeholder="Role / company" />
            <Button type="button" variant="ghost" size="sm" onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-red-600">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <select
              value={q.rating ?? 5}
              onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, rating: Number(e.target.value) } : x))}
              className="h-9 px-2 text-sm rounded-md border bg-background"
            >
              {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} star{r === 1 ? '' : 's'}</option>)}
            </select>
            <Input value={q.avatar_url || ''} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, avatar_url: e.target.value } : x))} placeholder="Avatar URL (optional)" />
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { id: `tst_${Date.now().toString(36)}`, quote: '', author: '', role: '', rating: 5 }])}>
        <Plus className="w-3.5 h-3.5 mr-1" /> Add testimonial
      </Button>
    </div>
  );
}

function HoursContactFields({ s, update }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Hours" value={s?.hours} onChange={(v) => update({ hours: v })} placeholder="Mon–Fri 9am–5pm" multiline rows={2} />
      <Field label="Address" value={s?.address} onChange={(v) => update({ address: v })} placeholder="123 Main St, Boise ID" multiline rows={2} />
      <Field label="Phone" value={s?.phone} onChange={(v) => update({ phone: v })} placeholder="(555) 555-0100" />
      <Field label="Email" value={s?.email} onChange={(v) => update({ email: v })} placeholder="hello@yourfirm.com" />
    </div>
  );
}

function ContactFormFields({ s, update }) {
  return (
    <>
      <Field label="Section title" value={s?.title} onChange={(v) => update({ title: v })} placeholder="Get in touch" />
      <Field label="Intro paragraph" value={s?.intro} onChange={(v) => update({ intro: v })} placeholder="Tell visitors what to expect when they reach out" multiline rows={2} />
    </>
  );
}

function FooterFields({ s, update }) {
  const social = s?.social || {};
  return (
    <>
      <Field label="Extra footer text" value={s?.extra_text} onChange={(v) => update({ extra_text: v })} placeholder="Licensed CPA in TX, CA, NY" />
      <div className="grid sm:grid-cols-2 gap-3">
        {['twitter', 'linkedin', 'instagram', 'facebook', 'website'].map((k) => (
          <Field key={k} label={k} value={social[k] || ''} onChange={(v) => update({ social: { ...social, [k]: v } })} placeholder={`https://${k}.com/…`} />
        ))}
      </div>
    </>
  );
}

function StatsFields({ s, update }) {
  const items = s?.items || [];
  const setItems = (next) => update({ items: next });
  return (
    <>
      <Field label="Section title (optional)" value={s?.title} onChange={(v) => update({ title: v })} placeholder="By the numbers" />
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <Input value={it.value || ''} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} placeholder="200+" className="w-32" />
            <Input value={it.label || ''} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Happy clients" className="flex-1" />
            <Button type="button" variant="ghost" size="sm" onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-red-600">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { value: '', label: '' }])}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add stat
        </Button>
        <p className="text-xs text-muted-foreground">Tip: use 3 or 4 stats — e.g. "$50M+ / Tax savings", "200+ / Happy clients", "12 yrs / Experience".</p>
      </div>
    </>
  );
}

function TrustFields({ s, update }) {
  const items = s?.items || [];
  const setItems = (next) => update({ items: next });
  return (
    <>
      <Field label="Section heading (optional)" value={s?.title} onChange={(v) => update({ title: v })} placeholder="Trusted by industry leaders" />
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <Input value={it.label || ''} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="QuickBooks ProAdvisor" className="flex-1" />
            <Input value={it.image_url || ''} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, image_url: e.target.value } : x))} placeholder="Logo URL (optional)" className="flex-1" />
            <Button type="button" variant="ghost" size="sm" onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-red-600">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { label: '', image_url: '' }])}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add badge
        </Button>
        <p className="text-xs text-muted-foreground">Use logos (paste image URL) or just text labels (certifications, partners, awards).</p>
      </div>
    </>
  );
}

function FAQFields({ s, update }) {
  const items = s?.items || [];
  const setItems = (next) => update({ items: next });
  return (
    <>
      <Field label="Section title" value={s?.title} onChange={(v) => update({ title: v })} placeholder="Frequently asked questions" />
      <div className="space-y-3">
        {items.map((q, i) => (
          <div key={i} className="rounded-lg border p-3 space-y-2">
            <Input value={q.question || ''} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, question: e.target.value } : x))} placeholder="Question" />
            <Textarea
              value={q.answer || ''}
              onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, answer: e.target.value } : x))}
              placeholder="Answer (1–3 sentences)"
              rows={3}
            />
            <div className="flex justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-red-600">
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { question: '', answer: '' }])}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add question
        </Button>
      </div>
    </>
  );
}

function Field({ label, value, onChange, placeholder, multiline, rows = 3 }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      {multiline ? (
        <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} />
      ) : (
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
}
